import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextInput } from '@mantine/core';
import { z } from 'zod';

import { Button } from '../buttons/Button';
import { ErrorMessage } from '../error-message/ErrorMessage';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';

import { LoginWidgetProps } from './LoginWidget';

import styles from './resetForm.module.scss';

type ResetFormProps = {
  isSso?: boolean;
  onBackToLogin: () => void;
  onEnterValidEmail?: LoginWidgetProps['onEnterValidEmail'];
  onSubmit: (email: string) => Promise<void>;
  error?: string;
};

export function ResetForm({
  isSso = false,
  onBackToLogin,
  onEnterValidEmail,
  onSubmit,
  error,
}: ResetFormProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');

  const [submitDisabled, setSubmitDisabled] = useState(false);

  const emailInvalid = !z.string().email().safeParse(email.trim()).success;

  useEffect(() => {
    if (onEnterValidEmail == null || emailInvalid) {
      return;
    }
    onEnterValidEmail(email.trim());
  }, [email, onEnterValidEmail, emailInvalid]);

  const submit: React.FormEventHandler = async (evt) => {
    evt.preventDefault();

    try {
      setSubmitDisabled(true);

      await onSubmit(email.trim());
    } finally {
      setSubmitDisabled(false);
    }
  };

  return (
    <form className={styles['root']} onSubmit={submit}>
      <span>{t('app.resetWidget.instructions')}</span>
      <TextInput
        autoComplete="email"
        label={t('app.resetWidget.emailLabel')}
        onChange={(event) => setEmail(event.currentTarget.value)}
        placeholder={t('app.resetWidget.emailPlaceholder') ?? undefined}
        tabIndex={1}
        value={email}
      />
      <div className={styles['root__cta-section']}>
        <Button
          icon={<GoogleFontIcon name="arrow_back_ios_new" small />}
          onClick={onBackToLogin}
          tabIndex={3}
          variant="text"
        >
          {t('app.resetWidget.back')}
        </Button>
        <Button
          disabled={emailInvalid || submitDisabled}
          enlarge
          tabIndex={2}
          type="submit"
        >
          {isSso ? t('app.resetWidget.submitSso') : t('app.resetWidget.submit')}
        </Button>
      </div>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </form>
  );
}
